// Add or edit one shop connection (a modal over /shops): platform, access,
// what the shop is used for (each use with the exact rights it needs), order
// options. A Shopify shop is connected by installing the Unanimity app on it
// (Shopify's own page) — nobody types a Shopify token; PrestaShop uses its
// Webservice key, never shown again once saved.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiPrestashop, SiShopify } from 'react-icons/si';
import { Store } from 'lucide-react';
import { CATALOG_USES, ORDER_USES, REQUIRES, RIGHTS, create, fail, update } from './api';
import { Banner, Modal, Switch } from '../../design/ui';

const BACKFILL_OPTIONS = [0, 7, 30, 60];

const initialForm = (shop, meta) => ({
    platform: shop?.platform || 'shopify',
    name: shop?.name || '',
    client_ref: shop?.client_ref || '',
    enabled: shop ? Boolean(shop.enabled) : true,
    uses: { orders: true, notes: false, tracking: false, products: false, prices: false, ...(shop?.uses || {}) },
    shop_domain: shop?.shop_domain || '',
    base_url: shop?.base_url || '',
    webservice_key: '',
    language_id: shop?.language_id || '1',
    vat_rate: shop?.vat_rate ?? 20,
    backfill_days: shop?.backfill_days ?? meta.limits?.default_backfill_days ?? 30,
    ship_from_postcode: shop?.ship_from_postcode || '',
    ship_from_country: shop?.ship_from_country || '',
});

// One use: what it does, the rights it needs on the shop, its switch.
const UseToggle = ({ use, form, setUse, t }) => {
    const needed = REQUIRES[use];
    const blocked = needed && !form.uses[needed];
    return (
        <label className={`shops-v2__use${blocked ? ' is-blocked' : ''}`}>
            <span className="shops-v2__use-text">
                <strong>{t(`shops.uses.${use}.label`, use)}</strong>
                <small>{t(`shops.uses.${use}.hint`, '')}</small>
                <code>{RIGHTS[form.platform][use]}</code>
            </span>
            <span className="u-switch">
                <input type="checkbox" checked={Boolean(form.uses[use]) && !blocked} disabled={blocked}
                    onChange={(e) => setUse(use, e.target.checked)} />
                <span className="u-switch__track" aria-hidden="true" />
            </span>
        </label>
    );
};

const PLATFORM_LOGO = {
    shopify: <SiShopify color="#95BF47" aria-hidden="true" />,
    prestashop: <SiPrestashop color="#DF0067" aria-hidden="true" />,
};

const ShopForm = ({ shop, meta, onSaved, onCancel }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState(() => initialForm(shop, meta));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const editing = Boolean(shop);
    const shopify = form.platform === 'shopify';

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const setUse = (use, on) => setForm((prev) => {
        const uses = { ...prev.uses, [use]: on };
        Object.entries(REQUIRES).forEach(([dependent, needed]) => { if (!uses[needed]) uses[dependent] = false; });
        return { ...prev, uses };
    });

    const credentialsOk = shopify
        ? meta.shopify_install_available && form.shop_domain.trim().length > 2
        : form.base_url.trim().startsWith('https://') && (form.webservice_key.trim() || (shop?.has_secret && shop?.base_url === form.base_url.trim().replace(/\/+$/, '')));
    const anyUse = Object.values(form.uses).some(Boolean);
    const ready = form.name.trim().length > 1 && credentialsOk && anyUse;

    const submit = async (e) => {
        e.preventDefault();
        if (!ready) return;
        const body = {
            platform: form.platform, name: form.name.trim(), client_ref: form.client_ref.trim() || null, enabled: form.enabled,
            uses: form.uses, backfill_days: Number(form.backfill_days),
            ship_from_postcode: form.ship_from_postcode.trim() || null, ship_from_country: form.ship_from_country.trim() || null,
        };
        if (shopify) {
            body.shop_domain = form.shop_domain.trim();
        } else {
            Object.assign(body, { base_url: form.base_url.trim(), webservice_key: form.webservice_key.trim() || null,
                language_id: form.language_id || '1', vat_rate: Number(form.vat_rate) });
        }
        setBusy(true);
        setError('');
        try {
            const res = editing ? await update(shop.id, body) : await create(body);
            if (res.data.install_url) {
                // Shopify asks the merchant to approve the app (or the new rights).
                window.location.assign(res.data.install_url);
                return;
            }
            onSaved(res.data, { created: !editing });
        } catch (err) {
            setError(fail(err, t('shops.saveError', "La boutique n'a pas pu être enregistrée.")));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            wide
            icon={Store}
            title={editing ? t('shops.editTitle', 'Modifier « {{name}} »', { name: shop.name }) : t('shops.addTitle', 'Connecter une boutique')}
            description={t('shops.subtitle', 'Connectez une fois chaque boutique Shopify ou PrestaShop : les commandes, la surveillance des colis et le catalogue s’en servent.')}
            onClose={() => { if (!busy) onCancel(); }}
            closeLabel={t('shops.cancel', 'Annuler')}
            footer={(
                <>
                    <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>{t('shops.cancel', 'Annuler')}</button>
                    <button type="submit" form="shops-v2-form" className="u-btn u-btn--primary" disabled={!ready || busy}>
                        {shopify && !editing && PLATFORM_LOGO.shopify}
                        {busy ? t('shops.saving', 'Enregistrement…')
                            : editing ? t('shops.save', 'Enregistrer')
                                : shopify ? t('shops.installShopify', 'Installer l’application sur Shopify') : t('shops.connect', 'Connecter et tester')}
                    </button>
                </>
            )}
        >
            <form id="shops-v2-form" className="shops-v2__form" onSubmit={submit}>
                {!editing && (
                    <div className="shops-v2__platforms" role="radiogroup" aria-label={t('shops.platform', 'Plateforme')}>
                        {(meta.platforms || []).map((p) => (
                            <button key={p.key} type="button" role="radio" aria-checked={form.platform === p.key}
                                className={`shops-v2__platform${form.platform === p.key ? ' is-selected' : ''}`}
                                onClick={() => set({ platform: p.key })}>
                                <span className="u-logo">{PLATFORM_LOGO[p.key] || <Store strokeWidth={1.8} aria-hidden="true" />}</span>
                                <strong>{p.label}</strong>
                            </button>
                        ))}
                    </div>
                )}

                <div className="u-form-grid">
                    <div className="u-field">
                        <label className="u-label" htmlFor="shops-name">{t('shops.name', 'Nom')}</label>
                        <input id="shops-name" className="u-input" value={form.name} maxLength={80} onChange={(e) => set({ name: e.target.value })}
                            placeholder={t('shops.namePlaceholder', 'Boutique FR')} />
                        {editing && <p className="u-help">{t('shops.key', 'Identifiant')} : <span className="u-code">{shop.key}</span></p>}
                    </div>
                    <div className="u-field">
                        <label className="u-label" htmlFor="shops-client">{t('shops.client', 'Client (marchand)')} {t('shops.optional', '(facultatif)')}</label>
                        <input id="shops-client" className="u-input" value={form.client_ref} maxLength={128} onChange={(e) => set({ client_ref: e.target.value })}
                            placeholder={t('shops.clientPlaceholder', 'Le marchand de cette boutique, si vous êtes logisticien')} />
                    </div>
                </div>

                <section className="shops-v2__section">
                    <h3 className="u-overline">{t('shops.access', 'Accès à la boutique')}</h3>
                    {shopify ? (
                        <>
                            <div className="u-field">
                                <label className="u-label" htmlFor="shops-domain">{t('shops.shopDomain', 'Domaine de la boutique')}</label>
                                <input id="shops-domain" className="u-input" value={form.shop_domain} onChange={(e) => set({ shop_domain: e.target.value })}
                                    placeholder="maboutique.myshopify.com" disabled={editing} />
                            </div>
                            {meta.shopify_install_available ? (
                                <p className="u-help">{t('shops.shopifyInstallHint', 'Vous serez dirigé vers Shopify pour installer l’application Unanimity sur cette boutique et approuver les droits des usages cochés ci-dessous. Aucun jeton à copier.')}</p>
                            ) : (
                                <Banner tone="warning">{t('shops.shopifyUnavailable', 'L’application Shopify n’est pas configurée sur ce serveur : contactez le support.')}</Banner>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="shops-v2__grid-3">
                                <div className="u-field">
                                    <label className="u-label" htmlFor="shops-url">{t('shops.shopUrl', 'Adresse de la boutique')}</label>
                                    <input id="shops-url" className="u-input" type="url" value={form.base_url} onChange={(e) => set({ base_url: e.target.value })} placeholder="https://www.maboutique.fr" />
                                </div>
                                <div className="u-field">
                                    <label className="u-label" htmlFor="shops-key">{t('shops.webserviceKey', 'Clé Webservice')}</label>
                                    <input id="shops-key" className="u-input" type="password" autoComplete="off" value={form.webservice_key} onChange={(e) => set({ webservice_key: e.target.value })}
                                        placeholder={shop?.has_secret ? t('shops.keepSecret', 'Laisser vide pour conserver') : ''} />
                                </div>
                                <div className="u-field">
                                    <label className="u-label" htmlFor="shops-lang">{t('shops.languageId', 'ID langue')}</label>
                                    <input id="shops-lang" className="u-input" inputMode="numeric" value={form.language_id} onChange={(e) => set({ language_id: e.target.value.replace(/\D/g, '') || '1' })} />
                                </div>
                            </div>
                            <p className="u-help">{t('shops.prestashopHint', 'Paramètres avancés › Webservice : activez le Webservice, créez une clé et cochez les droits des usages choisis ci-dessous. Aucun module à installer.')}</p>
                        </>
                    )}
                </section>

                <section className="shops-v2__section">
                    <h3 className="u-overline">{t('shops.usesTitle', 'Ce qu’Unanimity fait avec cette boutique')}</h3>
                    <div className="shops-v2__use-groups">
                        <div className="shops-v2__use-group">
                            <h4>{t('shops.groupOrders', 'Commandes et colis')}</h4>
                            {ORDER_USES.map((use) => <UseToggle key={use} use={use} form={form} setUse={setUse} t={t} />)}
                        </div>
                        {meta.catalog_enabled && (
                            <div className="shops-v2__use-group">
                                <h4>{t('shops.groupCatalog', 'Catalogue produits')}</h4>
                                {CATALOG_USES.map((use) => <UseToggle key={use} use={use} form={form} setUse={setUse} t={t} />)}
                            </div>
                        )}
                    </div>
                </section>

                {form.uses.orders && (
                    <section className="shops-v2__section">
                        <h3 className="u-overline">{t('shops.ordersOptions', 'Import des commandes')}</h3>
                        <div className="shops-v2__grid-3">
                            <div className="u-field">
                                <label className="u-label" htmlFor="shops-backfill">{t('shops.backfill', 'Première importation')}</label>
                                <select id="shops-backfill" className="u-select" value={form.backfill_days} onChange={(e) => set({ backfill_days: Number(e.target.value) })}>
                                    {BACKFILL_OPTIONS.map((d) => (
                                        <option key={d} value={d}>{d === 0 ? t('shops.backfillNone', 'Nouvelles commandes seulement') : t('shops.backfillDays', '{{days}} derniers jours', { days: d })}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="u-field">
                                <label className="u-label" htmlFor="shops-postcode">{t('shops.shipFromPostcode', 'Code postal d’expédition')}</label>
                                <input id="shops-postcode" className="u-input" maxLength={20} value={form.ship_from_postcode} onChange={(e) => set({ ship_from_postcode: e.target.value })} placeholder="75012" />
                            </div>
                            <div className="u-field">
                                <label className="u-label" htmlFor="shops-country">{t('shops.shipFromCountry', 'Pays')}</label>
                                <input id="shops-country" className="u-input" maxLength={2} value={form.ship_from_country} onChange={(e) => set({ ship_from_country: e.target.value.toUpperCase() })} placeholder="FR" />
                            </div>
                        </div>
                        <p className="u-help">{t('shops.shipFromHint', 'L’entrepôt d’où partent les colis : la surveillance colis s’en sert pour évaluer chaque colis (les boutiques ne le transmettent pas).')}</p>
                    </section>
                )}
                {!shopify && form.uses.products && (
                    <div className="u-field shops-v2__narrow">
                        <label className="u-label" htmlFor="shops-vat">{t('shops.vat', 'TVA (%) pour le prix TTC')}</label>
                        <input id="shops-vat" className="u-input" inputMode="decimal" value={form.vat_rate} onChange={(e) => set({ vat_rate: e.target.value.replace(/[^\d.]/g, '') })} />
                    </div>
                )}

                <Switch id="shops-enabled" checked={form.enabled} onChange={(checked) => set({ enabled: checked })} label={t('shops.enabled', 'Connexion active')} />

                {error && <Banner tone="danger">{error}</Banner>}
            </form>
        </Modal>
    );
};

export default ShopForm;
